import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SmModalWindow from '../../shared/modal_window'
import Spinner from '../../shared/spinner'
import CreateLocationForm from './create_location_form'
import { createLocation, resetCreateLocation } from '../../../actions/campaigns/locations_selector_actions'

const { I18n } = window as any

interface CreateLocationButtonProps {
  companyId: number
  countries: Array<{ label: string; value: string }>
  locationManagers: Array<{ label: string; value: number }>
}

function CreateLocationButton({ companyId, countries, locationManagers }: CreateLocationButtonProps) {
  const dispatch = useDispatch()
  const [dialogOpened, setDialogOpened] = useState(false)
  const [data, setData] = useState({
    name: '',
    number: '',
    address_attributes: {
      line1: '',
      line2: '',
      zip: '',
      city: '',
      country: '',
    },
  })

  const createLocationState = useSelector((state: any) => state.campaigns.createLocation)
  const { loading, error, success } = createLocationState

  const formRef = useRef(null)

  useEffect(() => {
    if (success) {
      // Auto-close modal on success after a brief delay to show success message
      const timer = setTimeout(() => {
        setDialogOpened(false)
        // Reset form data
        setData({
          name: '',
          number: '',
          address_attributes: {
            line1: '',
            line2: '',
            zip: '',
            city: '',
            country: '',
          },
        })
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [success])

  const handleSave = () => {
    const f = formRef.current
    const valid = f.checkValidity()
    if (!valid) {
      f.reportValidity()
    } else {
      // Add company_id to the data
      const locationData = {
        ...data,
        company_id: companyId,
      }
      dispatch(createLocation(locationData))
    }
    return valid
  }

  const handleClose = () => {
    setDialogOpened(false)
    // Reset form data on close
    setData({
      name: '',
      number: '',
      address_attributes: {
        line1: '',
        line2: '',
        zip: '',
        city: '',
        country: '',
      },
    })
    // Reset Redux state
    dispatch(resetCreateLocation())
  }

  const modalContent = (
    <>
      {error && <div className="text-danger text-center mb-3">{error}</div>}
      {success && <div className="text-success text-center mb-3">{I18n.t('application.messages.created_successfully')}</div>}
      {!success && (
        <div className="text-left">
          <CreateLocationForm data={data} setData={setData} formRef={formRef} countries={countries} locationManagers={locationManagers} />
        </div>
      )}
    </>
  )

  const modalButtons = (
    <div className="buttons-container">
      <button
        className="btn btn-secondary mr-2"
        type="button"
        disabled={loading}
        onClick={handleClose}
      >
        {I18n.t('application.actions.cancel')}
      </button>
      <button
        className="btn btn-primary"
        type="button"
        disabled={loading || success}
        onClick={handleSave}
      >
        {loading ? (
          <Spinner className="ml-3 mr-3" viewType="inline" size="1" />
        ) : (
          I18n.t('campaigns.others.create_location')
        )}
      </button>
    </div>
  )

  return (
    <>
      <button className="btn btn-outline-secondary" type="button" onClick={() => setDialogOpened(true)}>
        <i className="fa fa-plus mr-2" />
        {I18n.t('campaigns.others.create_location')}
      </button>
      <SmModalWindow
        title={I18n.t('campaigns.others.create_location')}
        visible={dialogOpened}
        onClose={handleClose}
        content={modalContent}
        buttons={modalButtons}
        windowID="campaign_create_location_popup"
      />
    </>
  )
}

export default CreateLocationButton
